import { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import CheckoutContext from '../lib/CheckoutContext';
import { getFreeTrialCartItemIds } from '../lib/cart';
import * as types from '../types';
import useHardwareFirstFreeTrial from './useHardwareFirstFreeTrial';
import useHardwareFirstShopCardsPricing from './useHardwareFirstShopCardsPricing';

interface HardwareFirstTrialUnits {
  /** Trial units in the cart; 0 outside the hardware-first trial. */
  count: number;
  /** What the trial units list at, charged 30 days after the order; 0 until the server has priced them. */
  listPriceInCents: number;
  /** Each trial sku's single-unit list price, for the row that names it. */
  listPriceBySku: Record<string, number | undefined>;
}

/**
 * The hardware-first trial bundles in a cart and what their devices list at. A trial bundle is not
 * a repriced hardware unit, so the list price comes from pricing its collar sku on its own. Reads the
 * checkout cart unless `cartOverride` is given, for the thank-you page's ordered cart.
 */
export default function useHardwareFirstTrialUnits(cartOverride?: types.Cart): HardwareFirstTrialUnits {
  const enabled = useHardwareFirstFreeTrial();
  const { cart: checkoutCart } = useContext(CheckoutContext);
  const cart = cartOverride ?? checkoutCart;
  const products = useSelector((state: types.AppState) => state.config.products);

  const items = useMemo(
    () => (enabled ? getFreeTrialCartItemIds(cart, products).map((id) => cart.cartItems[id]) : []),
    [enabled, cart, products],
  );
  const skus = useMemo(() => items.map((item) => item.lineItem.sku), [items]);
  const { discounts } = useHardwareFirstShopCardsPricing(skus, undefined);

  return useMemo(
    () => ({
      count: items.reduce((sum, item) => sum + item.quantity, 0),
      listPriceInCents: items.reduce(
        (sum, item) => sum + (discounts[item.lineItem.sku]?.listPriceInCents ?? 0) * item.quantity,
        0,
      ),
      listPriceBySku: Object.fromEntries(skus.map((sku) => [sku, discounts[sku]?.listPriceInCents])),
    }),
    [items, skus, discounts],
  );
}
